import { EMPLOYEE_PAGE_QUERY } from '@/constants/urlQuery';
import { mergeSearchParams, parseSearchParams } from '@/utils/urlSearch';

/**
 * @param {string} search - `location.search`
 * @returns {{ company: string | null, team: string | null, search: string | null, role: string | null }}
 */
export function getEmployeePageFiltersFromSearch(search) {
  const q = parseSearchParams(search);
  const str = (key) =>
    typeof q[key] === 'string' ? q[key].trim() || null : null;

  return {
    company: str(EMPLOYEE_PAGE_QUERY.company),
    team: str(EMPLOYEE_PAGE_QUERY.team),
    search: str(EMPLOYEE_PAGE_QUERY.search),
    role: str(EMPLOYEE_PAGE_QUERY.role),
  };
}

/**
 * `search`/`role` are only included when explicitly passed (not `undefined`)
 * so callers that only care about company/team (e.g. the Invite Employee
 * link) don't clobber a search/role value already in the URL.
 *
 * @param {{ companyId?: string, teamId?: string, search?: string, role?: string }} filters - empty string removes key
 * @returns {Record<string, string>}
 */
export function buildEmployeePageFilterUpdates({
  companyId,
  teamId,
  search,
  role,
}) {
  const updates = {
    [EMPLOYEE_PAGE_QUERY.company]: companyId ?? '',
    [EMPLOYEE_PAGE_QUERY.team]: teamId ?? '',
  };
  if (search !== undefined) updates[EMPLOYEE_PAGE_QUERY.search] = search ?? '';
  if (role !== undefined) updates[EMPLOYEE_PAGE_QUERY.role] = role ?? '';
  return updates;
}

/**
 * Merges company/team (and optionally search/role) into the current query
 * (keeps flow params).
 *
 * @param {string} currentSearch - `location.search`
 * @param {{ companyId?: string, teamId?: string, search?: string, role?: string }} filters
 * @returns {string}
 */
export function mergeEmployeePageFiltersIntoSearch(currentSearch, filters) {
  return mergeSearchParams(
    currentSearch,
    buildEmployeePageFilterUpdates({
      companyId: filters.companyId ?? '',
      teamId: filters.teamId ?? '',
      search: filters.search,
      role: filters.role,
    })
  );
}
